import { Combobox, Transition } from "@headlessui/react";
import debounce from "lodash/debounce";
import { Fragment, useState } from "react";

import { useAutoCompletion } from "@web-app/api";
import { Icon } from "@web-app/components";
import { GoogleAddress } from "@web-app/types";

type GoogleMapAutoCompletePropType = {
  address: GoogleAddress | null;
  disabled?: boolean;
  invalid?: boolean;
  isPublic?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  setAddress: (address: GoogleAddress | null) => Promise<void> | void;
};

const GoogleMapAutoComplete = ({
  address,
  disabled = false,
  invalid = false,
  isPublic = false,
  placeholder,
  readOnly = false,
  required = false,
  setAddress,
}: GoogleMapAutoCompletePropType) => {
  const [input, setInput] = useState(address?.description || "");
  const [isUpdating, setIsUpdating] = useState(false);

  const { data: addresses, isLoading } = useAutoCompletion(input, { isPublic });

  const debouncedSetInput = debounce((value: string) => {
    setInput(value);
  }, 300);

  const updateAddress = async (addr: GoogleAddress | null) => {
    setIsUpdating(true);

    try {
      const result = setAddress(addr);
      if (result instanceof Promise) {
        await result;
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const getAddressValue = () => {
    return isUpdating ? ({ description: input } as GoogleAddress) : address;
  };

  return (
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none z-10">
          <Icon
            name="locationPin"
            className="w-4 lg:w-5 h-4 lg:h-5 text-gray-400"
          />
        </div>
        <Combobox
          value={getAddressValue()}
          onChange={updateAddress}
          disabled={disabled || readOnly || isUpdating}
        >
          <div className="relative">
            <Combobox.Input
              className={`w-full rounded-md border ${
                invalid ? "border-red-500" : "border-gray-300"
              } ${
                isUpdating ? "opacity-50" : ""
              } py-2 pl-8 lg:pl-10 pr-3 text-xs lg:text-sm leading-5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                disabled || readOnly ? "bg-gray-100" : "bg-white"
              }`}
              displayValue={(address: GoogleAddress | null) =>
                address ? address.description : ""
              }
              onChange={(event) => {
                const value = event.target.value;
                debouncedSetInput(value);
              }}
              placeholder={placeholder}
              readOnly={readOnly}
              required={required}
            />
            {isUpdating && (
              <div className="absolute inset-y-0 right-0 flex items-center bg-white mt-1 mb-1 mr-1">
                <Icon
                  name="circleNotch"
                  className="h-3 w-3 text-black animate-spin m-2"
                />
              </div>
            )}
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs lg:text-sm">
              {isLoading ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  Loading...
                </div>
              ) : addresses?.length === 0 ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  No addresses found.
                </div>
              ) : (
                addresses?.map((addr) => (
                  <Combobox.Option
                    key={addr.place_id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-4 lg:pl-10 pr-4 ${
                        active ? "bg-blue-600 text-white" : "text-gray-900"
                      }`
                    }
                    value={addr}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {addr.description}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-blue-600"
                            }`}
                          >
                            <Icon name="fasCheck" className="w-5 h-5" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </Combobox>
      </div>
    </div>
  );
};

export default GoogleMapAutoComplete;
